import React, { useState, useRef, useEffect } from "react"
import Modal from "react-modal"
import Button from "src/component/button/button"
import CustomInput from "src/component/CustomInput/CustomInput"
import CustomSelect from "src/component/customSelect/customSelect"
import { IoMdClose } from "react-icons/io"
import { FaSpinner } from "react-icons/fa"
import { successMessage } from "src/utils/Helper"
import { createExamPaper, updateExamPaper } from "src/api/exam"
import { useNavigate, useParams } from "react-router-dom"
import { DateRange } from "react-date-range"
import dayjs from "dayjs"
import enUS from "date-fns/locale/en-US"

import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"

Modal.setAppElement("#root")

interface SubjectOption {
  label: string
  value: string
}

interface SaveExamModalProps {
  isOpen: boolean
  onClose: () => void
  loading: boolean
  buttonText: string
  subjects: SubjectOption[]
  topic_group: any
  examDetail: any
}

const complexities = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
]

const SaveExamModal: React.FC<SaveExamModalProps> = ({
  isOpen,
  onClose,
  buttonText,
  subjects,
  topic_group,
  examDetail,
}) => {
  const [title, setTitle] = useState<string>("")
  const [thumbnail, setThumbnail] = useState<File | null>(null)
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(
    null
  )
  const [error, setError] = useState<string | string[] | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [complexity, setComplexity] = useState<{
    label: string
    value: string
  } | null>(null)

  const [customDateRange, setCustomDateRange] = useState<{
    startDate: dayjs.Dayjs | null
    endDate: dayjs.Dayjs | null
  }>({
    startDate: null,
    endDate: null,
  })
  const [showCustomRangePicker, setShowCustomRangePicker] = useState(false)
  const customRangeRef = useRef<HTMLDivElement>(null)

  const { templateId } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    if (examDetail?._id) {
      setTitle(examDetail?.title)
      setThumbnailPreviewUrl(examDetail?.thumbnail)
      setComplexity(
        complexities.find((item) => item?.value === examDetail?.complexity) ??
          null
      )

      setCustomDateRange({
        endDate: dayjs(examDetail?.exam_scheduled_to),
        startDate: dayjs(examDetail?.exam_scheduled_from),
      })
    }
  }, [examDetail?._id])

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && !examDetail?._id) {
      setTitle("")
      setThumbnail(null)
      setThumbnailPreviewUrl(null)
      setError(null)
      setIsSubmitting(false)
      setComplexity(null)
      setCustomDateRange({ startDate: null, endDate: null })
      setShowCustomRangePicker(false)
    }
  }, [isOpen, examDetail?._id])

  //  Close picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        customRangeRef.current &&
        !customRangeRef.current.contains(event.target as Node)
      ) {
        setShowCustomRangePicker(false)
      }
    }
    if (showCustomRangePicker) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showCustomRangePicker])

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setThumbnail(file)
    if (file) {
      setThumbnailPreviewUrl(URL.createObjectURL(file))
    } else {
      setThumbnailPreviewUrl(null)
    }
  }

  const handleRemoveThumbnail = () => {
    setThumbnail(null)
    setThumbnailPreviewUrl(null)
    const fileInput = document.getElementById(
      "exam-thumbnail-input"
    ) as HTMLInputElement
    if (fileInput) fileInput.value = ""
  }

  const handleSubmit = async () => {
    setError(null)

    if (!title.trim()) {
      setError("Please enter a title.")
      return
    }

    if (!templateId && !examDetail?._id) {
      setError("Template ID is missing. Cannot save exam.")
      return
    }

    if (!complexity) {
      setError("Please select a complexity.")
      return
    }

    const start = customDateRange.startDate
    const end = customDateRange.endDate

    if (!start || !end) {
      setError("Please select a valid date range.")
      return
    }

    try {
      setIsSubmitting(true)

      const formData = new FormData()
      formData.append("title", title)
      formData.append(
        "status",
        buttonText.toLowerCase() === "save" ? "draft" : "published"
      )

      if (examDetail?._id) {
        formData.append(
          "topic_groups_json",
          JSON.stringify(examDetail?.topic_groups)
        )
      } else {
        formData.append("topic_groups_json", JSON.stringify(topic_group))
      }
      if (thumbnail) {
        formData.append("thumbnail", thumbnail)
      }

      formData.append("complexity", complexity.value)
      formData.append("exam_scheduled_from", start.startOf("day").toISOString())
      formData.append("exam_scheduled_to", end.endOf("day").toISOString())

      if (!examDetail?._id) {
        formData.append("template_id", templateId ?? "")
        await createExamPaper(formData)
        successMessage("Exam created successfully!")
        navigate("/exams")
        onClose()
        return
      }

      await updateExamPaper(examDetail?._id, formData)
      successMessage("Exam created successfully!")
      navigate("/exams")
      onClose()
    } catch (err: any) {
      console.error("Error creating exam:", err)
      if (
        err.response?.data?.detail &&
        Array.isArray(err.response.data.detail)
      ) {
        const errorMessages = err.response.data.detail.map((errDetail: any) => {
          if (errDetail.loc && errDetail.msg) {
            return `${errDetail.loc[errDetail.loc.length - 1]}: ${errDetail.msg}`
          }
          return errDetail.msg || "An unknown validation error occurred."
        })
        setError(errorMessages)
      } else {
        setError(
          err.response?.data?.message ||
            err.response?.data?.detail ||
            "Failed to save exam."
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName='fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4'
      className='bg-white rounded-md shadow-md w-full max-w-[500px] max-h-[calc(100vh-2rem)] outline-none flex flex-col'
    >
      <div className='p-6 overflow-y-auto'>
        <div className='flex justify-between items-center mb-4'>
          <h2 className='text-xl font-bold text-gray-800'>Save Exam Paper</h2>
          <button
            onClick={() => {
              onClose()
              setError(null)
            }}
            className='text-gray-500 hover:text-gray-700'
          >
            <IoMdClose size={24} />
          </button>
        </div>

        {error && (
          <div className='mb-4 text-red-600 text-sm font-medium'>
            {Array.isArray(error) ? (
              <ul>
                {error.map((msg, index) => (
                  <li key={index}>{msg}</li>
                ))}
              </ul>
            ) : (
              error
            )}
          </div>
        )}

        <div className='mb-4'>
          <label className='block mb-2 text-sm text-gray-700'>Title</label>
          <CustomInput
            type='text'
            placeholder='Enter exam title'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <label className='block mt-4 mb-2 text-sm text-gray-700'>
          Exam Thumbnail
        </label>
        {thumbnailPreviewUrl ? (
          <div className='relative w-full h-48 rounded-md overflow-hidden mb-6 border border-gray-300'>
            <img
              src={thumbnailPreviewUrl}
              alt='Exam Thumbnail Preview'
              className='w-full h-full object-cover'
            />
            <button
              onClick={handleRemoveThumbnail}
              className='absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 cursor-pointer hover:bg-red-600 focus:outline-none'
            >
              <IoMdClose size={18} />
            </button>
          </div>
        ) : (
          <input
            id='exam-thumbnail-input'
            type='file'
            accept='image/*'
            onChange={handleThumbnailChange}
            className='mb-6 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-100 file:text-red-700 hover:file:bg-blue-100'
          />
        )}

        <div className='mb-4'>
          <label className='block mb-2 text-sm text-gray-700'>Complexity</label>
          <CustomSelect
            data={complexities}
            value={complexity}
            onChange={setComplexity}
            placeholder='Select complexity level'
          />
        </div>

        <div className='mb-6 relative'>
          <label className='block mb-2 text-sm text-gray-700'>
            Exam Date Range
          </label>
          <div>
            <button
              onClick={() => setShowCustomRangePicker(true)}
              className='text-sm text-blue-600 underline mb-2 text-left'
            >
              {customDateRange.startDate && customDateRange.endDate
                ? `Selected: ${customDateRange.startDate.format("MMM D, YYYY")} → ${customDateRange.endDate.format("MMM D, YYYY")}`
                : "Select Date Range"}
            </button>

            {showCustomRangePicker && (
              <div
                ref={customRangeRef}
                style={{
                  position: "fixed",
                  zIndex: 9999,
                  background: "white",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  borderRadius: 8,
                  padding: "8px",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  maxWidth: "calc(100vw - 2rem)",
                  maxHeight: "calc(100vh - 2rem)",
                  overflow: "auto",
                }}
              >
                <DateRange
                  ranges={[
                    {
                      startDate:
                        customDateRange.startDate?.toDate() || new Date(),
                      endDate: customDateRange.endDate?.toDate() || new Date(),
                      key: "selection",
                    },
                  ]}
                  onChange={(item: any) => {
                    const range = item.selection
                    setCustomDateRange({
                      startDate: dayjs(range.startDate),
                      endDate: dayjs(range.endDate),
                    })
                  }}
                  locale={enUS}
                />
                <div style={{ marginTop: 8, textAlign: "right" }}>
                  <button
                    onClick={() => setShowCustomRangePicker(false)}
                    className='px-3 py-1 bg-blue-600 text-white rounded'
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='flex flex-wrap justify-end gap-3 p-6 pt-4 border-t border-gray-200'>
        <Button
          text={isSubmitting ? `${buttonText}...` : buttonText}
          type='primary'
          onClick={handleSubmit}
          disabled={isSubmitting}
          icon={
            isSubmitting ? <FaSpinner className='animate-spin' /> : undefined
          }
        />
        <Button
          icon={<IoMdClose />}
          text='Cancel'
          type='link'
          onClick={() => {
            onClose()
            setError(null)
          }}
          disabled={isSubmitting}
          className='text-red-700 bg-transparent text-[16px]'
        />
      </div>
    </Modal>
  )
}

export default SaveExamModal
